import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ICabinInfo,
  ICabinsState,
  IGuestFilterState,
} from '../../../../../interfaces/src/lib/interfaces/index';
import { FilterTypes } from '../../../../../interfaces/src/lib/enums/index';
interface QueryParams {
  adults?: string | null;
  minors?: string | null;
  juniors?: string | null;
  firstCabinAccessibility?: string | null;
  cabin2Adults?: string | null;
  cabin2Minors?: string | null;
  cabin2Juniors?: string | null;
  secondCabinAccessibility?: string | null;
  hasSecondCabin?: string | null;
  isConnectedCabin?: string | null;
}

function parseQueryInit(): IGuestFilterState {
  const urlParams = new URLSearchParams(window.location.search);
  const queryParams: QueryParams = {
    adults: urlParams.get('adults'),
    minors: urlParams.get('minors'),
    juniors: urlParams.get('juniors'),
    firstCabinAccessibility: urlParams.get('firstCabinAccessibility'),
    cabin2Adults: urlParams.get('cabin2Adults'),
    cabin2Minors: urlParams.get('cabin2Minors'),
    cabin2Juniors: urlParams.get('cabin2Juniors'),
    secondCabinAccessibility: urlParams.get('secondCabinAccessibility'),
    isConnectedCabin: urlParams.get('isConnectedCabin'),
  };

  const parseValues = (paramValue: string | null | undefined) => {
    if (!paramValue || isNaN(Number(paramValue))) return 0;
    if (parseInt(paramValue, 10) < 0) return 0;
    return parseInt(paramValue, 10);
  };

  return {
    firstCabin: {
      adults:
        !queryParams.adults ||
        isNaN(parseInt(queryParams.adults)) ||
        parseInt(queryParams.adults) < 1
          ? 2
          : parseValues(queryParams.adults),
      minors:
        !queryParams.minors ||
        isNaN(parseInt(queryParams.minors)) ||
        parseInt(queryParams.minors) < 0
          ? 0
          : parseValues(queryParams.minors),
      juniors:
        !queryParams.juniors ||
        isNaN(parseInt(queryParams.juniors)) ||
        parseInt(queryParams.juniors) < 0
          ? 0
          : parseValues(queryParams.juniors),
      isAccessible: queryParams.firstCabinAccessibility === 'true',
      useInSearch: true,
    },
    secondCabin: {
      isActive: !!(
        queryParams.cabin2Adults ||
        queryParams.cabin2Minors ||
        queryParams.cabin2Juniors
      ),
      adults:
        !queryParams.cabin2Adults ||
        isNaN(parseInt(queryParams.cabin2Adults)) ||
        parseInt(queryParams.cabin2Adults) < 1
          ? 2
          : parseValues(queryParams.adults),
      minors:
        !queryParams.cabin2Minors ||
        isNaN(parseInt(queryParams.cabin2Minors)) ||
        parseInt(queryParams.cabin2Minors) < 0
          ? 0
          : parseValues(queryParams.cabin2Minors),
      juniors:
        !queryParams.cabin2Juniors ||
        isNaN(parseInt(queryParams.cabin2Juniors)) ||
        parseInt(queryParams.cabin2Juniors) < 0
          ? 0
          : parseValues(queryParams.cabin2Juniors),
      isAccessible: !!queryParams.secondCabinAccessibility,
      isConnectedCabin: queryParams.isConnectedCabin === 'true',
      useInSearch: true,
    },
    pastBackup: undefined,
    aemConfigurations: {},
  };
}

const initialState: IGuestFilterState = parseQueryInit();

const guestFilterSlice = createSlice({
  name: 'guestFilter',
  initialState,
  reducers: {
    setFirstCabinState: (state, action: PayloadAction<ICabinInfo>) => {
      state.firstCabin = action.payload;
    },
    setSecondCabinState: (state, action: PayloadAction<ICabinInfo>) => {
      state.secondCabin = action.payload;
    },
    setChipLabelSingular: (state, action: PayloadAction<{ chipLabelSingular: string }>) => {
      state.aemConfigurations.chipLabelSingular = action.payload.chipLabelSingular;
    },
    setChipLabelPlural: (state, action: PayloadAction<{ chipLabelPlural: string }>) => {
      state.aemConfigurations.chipLabelPlural = action.payload.chipLabelPlural;
    },
    setSecondCabinChipLabel: (state, action: PayloadAction<{ secondCabinChipLabel: string }>) => {
      state.aemConfigurations.secondCabinChipLabel = action.payload.secondCabinChipLabel;
    },
    setGuestsFromBookingParams: (
      state,
      action: PayloadAction<{ adults: number; children: number; infants: number }>
    ) => {
      state.firstCabin.adults = action.payload.adults;
      state.firstCabin.minors = action.payload.children;
      state.firstCabin.juniors = action.payload.infants;
    },
    resetGuestFilter: (state, action: PayloadAction<string>) => {
      if (action.payload == FilterTypes.FIRST_CABIN_ACCESSIBILITY || action.payload == 'ALL_CABINS')
        state.firstCabin = {
          adults: 2,
          minors: 0,
          juniors: 0,
          isAccessible: false,
        };
      if (
        action.payload == FilterTypes.SECOND_CABIN_ACCESSIBILITY ||
        action.payload == 'ALL_CABINS'
      )
        state.secondCabin = {
          isActive: false,
          adults: 2,
          minors: 0,
          juniors: 0,
          isAccessible: false,
          isConnectedCabin: false,
        };
    },
    setOccupancyPastState: (state, action: PayloadAction<ICabinsState | undefined>) => {
      if (!state.pastBackup) state.pastBackup = action.payload;

      return state;
    },
    resetOccupancyPastState: (state) => {
      state.pastBackup = undefined;

      return state;
    },
    resetOccupancyToPastState: (state) => {
      if (state?.pastBackup?.firstCabin) state.firstCabin = state.pastBackup.firstCabin;
      if (state?.pastBackup?.secondCabin) state.secondCabin = state.pastBackup.secondCabin;

      state.pastBackup = undefined;

      return state;
    },
  },
});

export const {
  setFirstCabinState,
  setSecondCabinState,
  resetGuestFilter,
  setChipLabelPlural,
  setChipLabelSingular,
  setSecondCabinChipLabel,
  setOccupancyPastState,
  resetOccupancyPastState,
  resetOccupancyToPastState,
  setGuestsFromBookingParams,
} = guestFilterSlice.actions;
export default guestFilterSlice.reducer;
